import {
  ApiAutomaticSensor,
  ApiReturnAutomaticSensor,
  ApiReturnDeviceType,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnMeasureType,
} from '@highimpact-frontend/api-types';
import { Box, TextField, Typography } from '@mui/material';
import { forwardRef, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeviceService from '../../../../api/services/config/device/DeviceService';
import { resetExternalState } from '../../../../store/data/slice';
import { DataState } from '../../../../store/data/state';
import { useAppDispatch } from '../../../../store/hooks';
import FormGenerator, {
  FormGeneratorHandle,
} from '../../../../utils/formGenerator/FormGenerator';
import {
  FormElement,
  FormElementConfigW,
} from '../../../../utils/formGenerator/Types';
import { isEmptyObject } from '../../../../utils/functions';
import { createValidator, useForm } from '../../../../utils/hooks/useForm';
import { useSubmit } from '../../../../utils/hooks/useSubmit';
import CustomizedDialog from '../../../utils/MUISpecifics/dialog/CustumizedDialog';
import Stepper from '../../Stepper';
import Step1 from './Step1';

/* ---------- Types ---------- */

interface EditAutomaticDeviceProps {
  onCancel: () => void;
  isOpen: boolean;
  closeModal: () => void;
  automaticSensor: ApiReturnAutomaticSensor;
  models: string[];
  deviceTypes: ApiReturnDeviceType[];
  measureTypes: DataState<string, { [KEY in number]: ApiReturnMeasureType }>;
  deviceTypeMeasureTypes: DataState<
    string,
    { [KEY in number]: ApiReturnDeviceTypeMeasureType }
  >;
}

export type AutomaticSensorData = {
  automaticSensor: ApiAutomaticSensor;
  configuration: object;
};

export type JSONFieldType = 'string' | 'number' | 'password' | 'boolean';

export type JSONStructureType = Record<
  string,
  { type: JSONFieldType; pretty_name: string }
>;

export type JSONToType<T extends JSONStructureType> = {
  [K in keyof T]: T[K]['type'] extends 'string' | 'password'
    ? string
    : T[K]['type'] extends 'number'
    ? number
    : T[K]['type'] extends 'boolean'
    ? boolean
    : never;
};

/* ---------- Utility functions ---------- */

/** Maps JSON type to corresponding form element. */
const jsonTypeToConfigType = <T extends JSONFieldType>(
  type: T
): FormElement => {
  switch (type) {
    case 'string':
      return 'TexInput';
    case 'password':
      return 'Password';
    case 'number':
      return 'NumberInput';
    case 'boolean':
      return 'Switch';
    default:
      throw new Error(`Unsupported JSON type: ${type}`);
  }
};

/** Maps JSON type to corresponding field validator(s). */
const jsonTypeToValidator = <T extends object>(
  fieldName: keyof T,
  type: JSONFieldType
): ((values: T) => string | undefined)[] => {
  switch (type) {
    case 'string':
    case 'password':
      return [
        (values: T) =>
          (values[fieldName] as string)?.length === 0
            ? 'Entrer plus qu’un caractère'
            : undefined,
      ];
    case 'number':
    case 'boolean':
      return [];
    default:
      throw new Error(`Unsupported JSON type: ${type}`);
  }
};

/** Converts a JSON schema into a form configuration array. */
function jsonToConfig<T extends object>(
  schema: JSONStructureType
): FormElementConfigW<T>[] {
  return Object.entries(schema).map(([key, value]) => ({
    elementType: jsonTypeToConfigType(value.type),
    fields: [key as keyof T],
    transformator: undefined,
    validator: jsonTypeToValidator(key as keyof T, value.type),
    label: value.pretty_name,
    placeHolder: `Saisir ${value.pretty_name?.toLowerCase?.() || ''}`,
  })) as FormElementConfigW<T>[];
}

/* ---------- Main Component ---------- */

/**
 * Modal component for creating a new automatic sensor device in multiple steps.
 */
function UpdateAutomaticDevice({
  onCancel,
  isOpen,
  deviceTypes,
  models,
  measureTypes,
  deviceTypeMeasureTypes,
  automaticSensor,
}: EditAutomaticDeviceProps): JSX.Element {
  const { t } = useTranslation('components.devices');

  const [isErrorOnForm, setIsErrorOnForm] = useState<boolean>(false);
  const [lastStepSubmited, setLastStepSubmited] = useState<boolean>(false);
  const [retrieval_frequency, setRetrievalFrequency] = useState<number | ''>(
    automaticSensor.configuration?.generic_configuration?.retrieval_frequency ??
      ''
  );
  const dispatch = useAppDispatch();
  const { submit } = useSubmit();
  const dataRef = useRef<AutomaticSensorData>({
    automaticSensor: automaticSensor,
    configuration: automaticSensor.configuration
      ?.specific_configuration as object,
  });

  /** Provides default values for each JSON field type. */
  function defaultValuesFromConfig<T extends object>(
    schema: JSONStructureType,
    device: ApiAutomaticSensor
  ): Record<keyof T, string | null | boolean | number> {
    return Object.entries(schema).reduce((acc, [key, value]) => {
      const specificConfig = device.configuration?.specific_configuration ?? {};
      let defaultValue: string | null | boolean | number;

      switch (value.type) {
        case 'string':
        case 'password':
          defaultValue = (specificConfig[key] as string) ?? '';
          break;
        case 'number':
          defaultValue = (specificConfig[key] as number) ?? 0;
          break;
        case 'boolean':
          defaultValue = (specificConfig[key] as unknown as boolean) ?? false;
          break;
        default:
          defaultValue = null;
      }

      acc[key as keyof T] = defaultValue;
      return acc;
    }, {} as Record<keyof T, string | null | boolean | number>);
  }

  const deviceConfiguration = dataRef.current.automaticSensor.device_type_id
    ? deviceTypes.find(
        (dt) => dt.id === dataRef.current.automaticSensor.device_type_id
      )?.configuration
    : {};

  const formGeneratorRef = useRef<FormGeneratorHandle>(null);

  /** Updates part of the device creation state. */
  const updateData = <T extends keyof AutomaticSensorData>(
    key: T,
    value: AutomaticSensorData[T]
  ) => {
    dataRef.current[key] = value;
  };

  /** Final submission handler. */
  const onSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const deviceToAdd = {
      ...dataRef.current.automaticSensor,
      configuration: {
        generic_configuration:
          retrieval_frequency !== ''
            ? {
                ...dataRef.current.automaticSensor.configuration!
                  .generic_configuration!,
                retrieval_frequency,
              }
            : null,
        specific_configuration: {
          ...dataRef.current.configuration,
        },
      },
    };

    submit({
      callbacks: [
        async () => await DeviceService.update(automaticSensor.id, deviceToAdd),
        () => dispatch(resetExternalState({ external_key: 'devices' })),
        () => dispatch(resetExternalState({ external_key: 'site_devices' })),
        () => dispatch(resetExternalState({ external_key: 'sites' })),
        () => onCancel(),
      ],
      errorMessages: {
        anyError: t(
          'Une erreur est survenue lors de la modification du capteur connecté.'
        ),
        unicityError: t(
          'Un capteur connecté avec le même modèle et numéro de série existe déjà'
        ),
      },
      successMessage: t('Le capteur connecté a été modifié avec succès'),
    });
  };

  /** Form submission handler for device information. */
  const onAutomaticDeviceSubmit = async (data: ApiAutomaticSensor) => {
    updateData('automaticSensor', data);
  };

  /** Form submission handler for configuration data. */
  const onFormGeneratorSubmit = async (data: object) => {
    updateData('configuration', data);
  };

  /** Device info validation rules. */
  const AutomaticDeviceValidator = createValidator<ApiAutomaticSensor>({
    name: [
      (n) =>
        !n || n.trim().length < 4
          ? t('Entrer au moins 4 caractères')
          : undefined,
    ],
    brand: [
      (n) =>
        !n || !n.trim().length
          ? t('Choisir un modèle ou entrer des caractères valides')
          : undefined,
    ],
    type: [(n) => (n === null || !n ? 'un nombre' : undefined)],
    sn: [
      (n) =>
        !n || n.length < 4 ? t('Entrer au moins 4 caractères') : undefined,
    ],
    device_type_id: [
      (n) => (n === null || !n ? t('Choisir un type de capteur') : undefined),
    ],
  });

  const AutomaticDeviceMethods = useForm(
    automaticSensor,
    AutomaticDeviceValidator,
    onAutomaticDeviceSubmit
  );

  /** Handles logic when the user clicks "Next" on the stepper. */
  const handleNext = (
    step: number,
    e: React.MouseEvent<HTMLButtonElement>
  ): boolean | undefined => {
    if (step === 0) {
      return (
        AutomaticDeviceMethods.onSubmit as (
          e: React.MouseEvent<HTMLButtonElement>
        ) => boolean
      )(e);
    } else if (step === 1) {
      const state = !formGeneratorRef.current?.triggerSubmit();
      setLastStepSubmited(true);
      setIsErrorOnForm(state);
      return !state;
    }
  };

  const jsonSchema = deviceConfiguration as JSONStructureType;
  const formConfig = jsonToConfig(jsonSchema);

  /** Renders the appropriate content per step in the process. */
  const renderStepContent = (activeStep: number): JSX.Element | null => {
    switch (activeStep) {
      case 0:
        return (
          <Step1
            form={AutomaticDeviceMethods}
            deviceTypeMeasureTypes={deviceTypeMeasureTypes}
            device_types={deviceTypes}
            measureTypes={measureTypes}
            models={models}
            device={automaticSensor}
          />
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('Retrieval frequency (s)')}
              type="number"
              value={retrieval_frequency}
              onChange={(e) =>
                setRetrievalFrequency(
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
              inputProps={{ min: 1 }}
              fullWidth
            />
            {!isEmptyObject(formConfig) ? (
              <FormGenerator
                defaultValues={
                  Object.keys(dataRef.current.configuration).length === 0
                    ? defaultValuesFromConfig(jsonSchema, automaticSensor)
                    : dataRef.current.configuration
                }
                onSubmit={onFormGeneratorSubmit}
                config={formConfig}
                ref={formGeneratorRef}
                withButton={false}
              />
            ) : (
              <Typography>
                Ce type de capteur n'a pas de configuration
              </Typography>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        padding: '3em',
        border: '1px solid #ccc',
        borderRadius: '0.3em',
      }}
    >
      <CustomizedDialog
        onCancel={onCancel}
        open={isOpen}
        title={t('Modifier un capteur connecté')}
      >
        <Stepper
          steps={[t('Informations générales'), t('Réception des données')]}
          renderStepContent={renderStepContent}
          onNext={handleNext}
          onsubmit={onSubmit}
          isAllGood={isErrorOnForm}
          submitted={lastStepSubmited}
        />
      </CustomizedDialog>
    </div>
  );
}

export default forwardRef(UpdateAutomaticDevice);
